import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { useSettingsStore, useAuthStore, useCrmStore } from '@/stores';
import { BusinessConfig } from '@/components/settings/BusinessConfig';
import { MessagingConfig } from '@/components/settings/MessagingConfig';
import { AppointmentsConfig } from '@/components/settings/AppointmentsConfig';
import { CrmConfig } from '@/components/settings/CrmConfig';
import { RateConfig } from '@/components/settings/RateConfig';
import { SystemConfig } from '@/components/settings/SystemConfig';
import { Button } from '@/components/common/Button';
import { useToast } from '@/components/common/Toast';
import { ScrollableContainer } from '@/components/common/ScrollableContainer';
import {
  Building2, MessageSquare, CalendarClock, Users, Coins,
  ShieldCheck, Cloud, CloudUpload, Loader2
} from 'lucide-react';
import { clsx } from 'clsx';
import * as api from '@/lib/api';
import { getAppMeta, setAppMeta } from '@/lib/db';
import type { CrmSegment } from '@/types/crm';

export type SettingsTabId = 'business' | 'messaging' | 'appointments' | 'crm' | 'rates' | 'system';

interface SettingsTabDef {
  id: SettingsTabId;
  label: string;
  shortLabel: string;
  description: string;
  icon: React.ReactNode;
}

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTabId>('business');
  const [isSavingRemote, setIsSavingRemote] = useState(false);

  const settings = useSettingsStore();
  const fetchRemoteSettings = useSettingsStore((s) => s.fetchRemoteSettings);
  const { user, workspaceId } = useAuthStore();
  const { showToast } = useToast();

  const isAdmin = user?.role === 'admin';

  // Fetch remote settings on mount
  useEffect(() => {
    fetchRemoteSettings();
  }, [fetchRemoteSettings]);

  // Ensure workspace segments are loaded for CRM settings
  useEffect(() => {
    if (!workspaceId) return;

    getAppMeta<CrmSegment[]>('crm_segments')
      .then((cached) => {
        if (cached && cached.length > 0) {
          useCrmStore.getState().setSegments(cached);
        }
      })
      .catch(console.warn);

    api
      .getSegments(workspaceId)
      .then((res) => {
        const edges =
          res?.data?.segmentDefinitions?.edges ||
          res?.data?.data?.segmentDefinitions?.edges ||
          [];
        const segs: CrmSegment[] = edges
          .map((e: any) => ({
            id: String(e.node?.id || ''),
            name: e.node?.name || '',
            description: e.node?.description || '',
            rules: e.node?.rules || [],
            count: typeof e.node?.customerCount === 'number' ? e.node.customerCount : undefined,
          }))
          .filter((s: CrmSegment) => Boolean(s.id));

        if (segs.length > 0) {
          useCrmStore.getState().setSegments(segs);
          setAppMeta('crm_segments', segs).catch(console.warn);
        }
      })
      .catch(console.warn);
  }, [workspaceId]);

  const handleSaveRemote = async () => {
    setIsSavingRemote(true);
    try {
      const res = await settings.saveRemoteSettings();
      if (res.success) {
        showToast('Ajustes guardados en la nube exitosamente', 'success');
      } else {
        showToast(res.error || 'Error al guardar ajustes en la nube', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Error de conexión', 'error');
    } finally {
      setIsSavingRemote(false);
    }
  };

  const tabs: SettingsTabDef[] = [
    {
      id: 'business',
      label: 'Negocio & Conexión',
      shortLabel: 'Negocio',
      description: 'Identidad del negocio y vinculación con Fresha',
      icon: <Building2 size={16} />,
    },
    {
      id: 'messaging',
      label: 'Mensajería & WhatsApp',
      shortLabel: 'Mensajería',
      description: 'Enlace WA, tratamiento de nombres, saludos y horas',
      icon: <MessageSquare size={16} />,
    },
    {
      id: 'appointments',
      label: 'Citas & Agenda',
      shortLabel: 'Citas',
      description: 'Estrategia y ciclo de vida de 9 estados',
      icon: <CalendarClock size={16} />,
    },
    {
      id: 'crm',
      label: 'CRM, Segmentos & Reglas',
      shortLabel: 'CRM & Clientes',
      description: 'Reglas métricas, plantillas de comportamiento y segmentos',
      icon: <Users size={16} />,
    },
    {
      id: 'rates',
      label: 'Monedas & Precios',
      shortLabel: 'Monedas',
      description: 'Moneda base, tasa BCV, recargo y precios en WA',
      icon: <Coins size={16} />,
    },
    {
      id: 'system',
      label: 'Seguridad & Sistema',
      shortLabel: 'Sistema',
      description: 'Mi cuenta, personal, clave de rescate, tema y reseteo',
      icon: <ShieldCheck size={16} />,
    },
  ];

  const currentTab = tabs.find((t) => t.id === activeTab) || tabs[0];

  return (
    <div className="space-y-5 py-4 max-w-5xl mx-auto w-full">
      {/* Page Header */}
      <div>
        <h2 className="text-xl font-bold text-text-primary tracking-tight">Centro de Configuración</h2>
        <p className="text-xs text-text-muted">
          Personaliza el motor de WhatsApp, plantillas inteligentes, monedas y accesos
        </p>
      </div>

      {/* Cloud Sync Status Header Card */}
      <div className="bg-surface border border-border rounded-xl p-3.5 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={clsx(
            'w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-2xs',
            settings.lastSyncedAt ? 'bg-state-confirmed/10 text-state-confirmed border border-state-confirmed/20' : 'bg-brand-primary/10 text-brand-primary'
          )}>
            <Cloud size={18} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-text-primary">Almacenamiento Seguro en la Nube</span>
              <span className={clsx(
                'text-[10px] px-1.5 py-0.2 rounded-full font-semibold',
                isAdmin ? 'bg-brand-primary/10 text-brand-primary' : 'bg-surface-secondary text-text-secondary'
              )}>
                {isAdmin ? 'Modo Administrador' : 'Solo Lectura'}
              </span>
            </div>
            <p className="text-[11px] text-text-muted">
              {settings.lastSyncedAt
                ? `Última sincronización: ${dayjs(settings.lastSyncedAt).format('DD/MM/YYYY HH:mm')}`
                : 'Cambios locales guardados en navegador. Pendiente de sincronizar.'}
            </p>
          </div>
        </div>

        {isAdmin && (
          <Button
            type="button"
            size="sm"
            disabled={isSavingRemote || settings.isSyncing}
            onClick={handleSaveRemote}
            className="text-xs shrink-0 self-end sm:self-auto shadow-2xs"
          >
            {isSavingRemote || settings.isSyncing ? (
              <>
                <Loader2 size={13} className="animate-spin mr-1" />
                <span>Guardando...</span>
              </>
            ) : (
              <>
                <CloudUpload size={14} className="mr-1.5" />
                <span>Guardar en la Nube</span>
              </>
            )}
          </Button>
        )}
      </div>

      {/* Main Adaptive Layout (Tabs + Content) */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-start">
        {/* Navigation Column */}
        <div className="md:col-span-4 lg:col-span-3 space-y-1">
          {/* Mobile Pill Scroll */}
          <div className="md:hidden">
            <ScrollableContainer
              buttonVariant="capsule"
              className="bg-surface-secondary rounded-xl p-1 border border-border/60"
              scrollClassName="gap-1.5"
              scrollAmount={140}
            >
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={clsx(
                    'min-w-[105px] py-2 px-3 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap shrink-0',
                    activeTab === tab.id
                      ? 'bg-surface text-brand-primary shadow-xs'
                      : 'text-text-secondary hover:text-text-primary'
                  )}
                >
                  {tab.icon}
                  <span>{tab.shortLabel}</span>
                </button>
              ))}
            </ScrollableContainer>
          </div>

          {/* Desktop Sidebar Navigation List */}
          <div className="hidden md:flex flex-col gap-1 bg-surface border border-border rounded-2xl p-2 shadow-xs">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={clsx(
                    'w-full text-left p-3 rounded-xl transition-all cursor-pointer flex items-center gap-3',
                    isActive
                      ? 'bg-brand-primary text-white shadow-xs font-semibold'
                      : 'text-text-secondary hover:text-text-primary hover:bg-surface-secondary/70'
                  )}
                >
                  <div className={clsx(
                    'w-7 h-7 rounded-lg flex items-center justify-center shrink-0',
                    isActive ? 'bg-white/20 text-white' : 'bg-surface-secondary text-brand-primary'
                  )}>
                    {tab.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs truncate">{tab.label}</div>
                    <div className={clsx(
                      'text-[10px] truncate',
                      isActive ? 'text-white/80' : 'text-text-muted'
                    )}>
                      {tab.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active Content Module */}
        <div className="md:col-span-8 lg:col-span-9 space-y-4 animate-fade-in">
          {/* Section Header */}
          <div className="bg-surface/60 backdrop-blur-xs border border-border rounded-xl p-3 flex items-center justify-between gap-2 shadow-2xs">
            <div className="flex items-center gap-2">
              <div className="text-brand-primary">{currentTab.icon}</div>
              <div>
                <h3 className="text-xs font-bold text-text-primary">{currentTab.label}</h3>
                <p className="text-[10px] text-text-muted">{currentTab.description}</p>
              </div>
            </div>
          </div>

          {/* Module Content */}
          {activeTab === 'business' && <BusinessConfig />}
          {activeTab === 'messaging' && <MessagingConfig />}
          {activeTab === 'appointments' && <AppointmentsConfig />}
          {activeTab === 'crm' && <CrmConfig />}
          {activeTab === 'rates' && <RateConfig />}
          {activeTab === 'system' && <SystemConfig />}
        </div>
      </div>
    </div>
  );
}
